import React, { Component } from 'react';
import { Moment } from 'moment';
import api from '../util/api';
import LoadingSpinner from '../elements/LoadingSpinner';
import { Box } from 'bloomer/lib/elements/Box';
import { Title } from 'bloomer/lib/elements/Title';
import { Content } from 'bloomer/lib/elements/Content';
import Bold from '../elements/Bold';
import { Field } from 'bloomer/lib/elements/Form/Field/Field';
import { Label } from 'bloomer/lib/elements/Form/Label';
import { Control } from 'bloomer/lib/elements/Form/Control';
import { Select } from 'bloomer/lib/elements/Form/Select';
import { Button } from 'bloomer/lib/elements/Button';
import MultiSelect from 'react-select';
import { ValueType } from 'react-select/lib/types';
import { popupErrorFromServer } from '../util/helperFunctions';

interface PropTypes {
  startDate: Moment;
  endDate: Moment;
  animalType: animalType | '';
  handleSubmit: (getFilters: () => any) => (event: React.FormEvent<HTMLFormElement>) => void;
  setStep: (num: 1 | 2 | 3) => void;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  exludeTimeMax: number;
}

interface StateTypes {
  enrichmentTypes: {
    title: 'Enrichment Types: ';
    id: 'enrichmentTypes';
    values: {
      label: string;
      value: string;
    }[];
  };
  locations: {
    title: 'Locations: ';
    id: 'locationAtTimeOfEnrichment';
    values: {
      label: string;
      value: string;
    }[];
  };
  selectValues: {
    enrichmentTypes: {
      label: string;
      value: string;
    }[];
    locationAtTimeOfEnrichment: {
      label: string;
      value: string;
    }[];
  };
  loading: boolean;
}

class ReportOptions extends Component<PropTypes, StateTypes> {
  state: StateTypes = {
    enrichmentTypes: {
      title: 'Enrichment Types: ',
      id: 'enrichmentTypes',
      values: [],
    },
    locations: {
      title: 'Locations: ',
      id: 'locationAtTimeOfEnrichment',
      values: [],
    },
    selectValues: {
      enrichmentTypes: [],
      locationAtTimeOfEnrichment: [],
    },
    loading: true,
  };

  async componentDidMount() {
    try {
      const { startDate, endDate } = this.props;
      const { data } = await api.enrichmentData.distincts({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      });
      const {
        enrichmentTypes,
        locations,
      }: {
        enrichmentTypes: string[];
        locations: string[];
        animalTypes: string[];
      } = data;

      const formatForSelect = (item: string) => ({ label: item, value: item });
      const enrichmentTypeValues = enrichmentTypes.map(formatForSelect);
      const locationValues = locations.map(formatForSelect);
      this.setState({
        enrichmentTypes: {
          title: 'Enrichment Types: ',
          id: 'enrichmentTypes',
          values: enrichmentTypeValues,
        },
        locations: {
          title: 'Locations: ',
          id: 'locationAtTimeOfEnrichment',
          values: locationValues,
        },
        selectValues: {
          // Set the initial values to all of the possible values
          enrichmentTypes: enrichmentTypeValues,
          locationAtTimeOfEnrichment: locationValues,
        },
        loading: false,
      });
    } catch (err) {
      popupErrorFromServer(err);
    }
  }

  handleMultiSelectChange = (id: 'enrichmentTypes' | 'locationAtTimeOfEnrichment') => (
    value: ValueType<{
      label: string;
      value: string;
    }>,
  ) => {
    const values = Object.assign({}, this.state.selectValues);
    values[id] = value as {
      label: string;
      value: string;
    }[];
    this.props.setStep(2);
    this.setState({
      selectValues: values,
    });
  };

  getFilters = () => {
    const { selectValues } = this.state;
    const filters = Object.entries(selectValues).reduce((obj, [key, val]) => {
      return {
        ...obj,
        [key === 'enrichmentTypes' ? 'enrichmentType' : key]: val.map(({ value }) => value),
      };
    }, {});
    return filters;
  };

  render() {
    const { enrichmentTypes, locations, selectValues } = this.state;
    const selects = [enrichmentTypes, locations];
    const { handleSubmit } = this.props;
    if (this.state.loading) {
      return <LoadingSpinner />;
    }

    return (
      <Box className="hide-on-print">
        <Content>
          <Title tag="h2">Report Options</Title>
          <p>
            Below you can customize what data will be used to generate the report. These options will be populated with
            all possible options for a given category by default. You can remove particular entries by clicking the X
            next to them, and you can re-add entries by clicking the chevron and selecting your desired entry from the
            list.
          </p>
          <p>
            <Bold>Note:</Bold> You may notice duplicate entires if one has an added space(s) or different
            capitalization, however these differences will be normalized for reporting. EX. "MC" and "mc" will be
            treated as the same room.
          </p>
        </Content>
        <form onSubmit={handleSubmit(this.getFilters)}>
          <Field>
            <Label htmlFor="animalType">Animal Type</Label>
            <Control>
              <Select
                onChange={this.props.handleChange}
                value={this.props.animalType}
                name="animalType"
                id="animalType"
                required={true}
              >
                <option value=""> - </option>
                <option value="dog"> Dogs </option>
                <option value="cat"> Cats </option>
              </Select>
            </Control>
          </Field>
          <Field>
            <Label htmlFor="excludeTime">Exclude times exceeding this value:</Label>
            <Control>
              <input
                onChange={this.props.handleChange}
                value={this.props.exludeTimeMax}
                type="range"
                name="exludeTimeMax"
                id="exludeTimeMax"
                min="1"
                max="5000"
              />
              <input
                onChange={this.props.handleChange}
                value={this.props.exludeTimeMax}
                name="exludeTimeMax"
                type="number"
                style={{ border: 'none' }}
              />
            </Control>
          </Field>
          {selects.map(({ title, id, values }) => (
            <Field key={id}>
              <Label htmlFor={id}>{title}</Label>
              <Control>
                <MultiSelect
                  className="reportOptionsMultiSelect is-size-3"
                  value={selectValues[id]}
                  isMulti={true}
                  name={id}
                  options={values}
                  classNamePrefix="select"
                  onChange={this.handleMultiSelectChange(id)}
                />
              </Control>
            </Field>
          ))}
          <Button isColor="primary" type="submit">
            Generate Report
          </Button>
        </form>
      </Box>
    );
  }
}

export default ReportOptions;
